import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaBed,
  FaChartLine,
  FaClipboardCheck,
  FaMoneyBillWave,
  FaQrcode,
  FaSync,
  FaUsers,
} from 'react-icons/fa';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  StatCard,
  StatContent,
  StatIcon,
  StatLabel,
  StatsGrid,
  StatValue,
} from '../../styles/AdminStyles';
import {
  fetchAdminJson,
  formatCurrency,
  getAdminHeaders,
} from './campAdminShared';

const CampAdminDashboard = () => {
  const [statistics, setStatistics] = useState(null);
  const [checkInOverview, setCheckInOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadDashboard = async () => {
    try {
      setError('');
      const [statsData, checkInData] = await Promise.all([
        fetchAdminJson('/camp-registration/statistics', {
          headers: getAdminHeaders(),
        }),
        fetchAdminJson('/camp-registration/check-in-overview', {
          headers: getAdminHeaders(),
        }),
      ]);

      setStatistics(statsData);
      setCheckInOverview(checkInData);
    } catch (loadError) {
      console.error('Error loading camp dashboard:', loadError);
      setError(loadError.message || 'Failed to load camp dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboard();
  };

  if (loading) {
    return (
      <AdminLayout title="Camp Dashboard" subtitle="Loading...">
        <LoadingState>Loading camp dashboard...</LoadingState>
      </AdminLayout>
    );
  }

  const selectedDaySummary = checkInOverview?.daySummaries?.find(
    (day) => day.dayKey === checkInOverview?.selectedDay?.dayKey,
  );

  return (
    <AdminLayout
      title="Camp Dashboard"
      subtitle="Camp stats live here. Registrations, QR codes, and check-ins are on their own pages."
    >
      {error && (
        <Alert $type="error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </Alert>
      )}

      <PageToolbar>
        <ToolbarText>
          Monitor attendance, payments, and room usage without mixing it into
          registration management.
        </ToolbarText>
        <Button onClick={handleRefresh} disabled={refreshing}>
          <FaSync className={refreshing ? 'spin' : ''} /> Refresh
        </Button>
      </PageToolbar>

      {statistics && (
        <StatsGrid style={{ marginBottom: '1.5rem' }}>
          <StatCard>
            <StatIcon $color="#2563eb">
              <FaUsers />
            </StatIcon>
            <StatContent>
              <StatValue>{statistics.totalRegistrations || 0}</StatValue>
              <StatLabel>Total Campers</StatLabel>
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon $color="#16a34a">
              <FaMoneyBillWave />
            </StatIcon>
            <StatContent>
              <StatValue>{formatCurrency(statistics.totalRevenue)}</StatValue>
              <StatLabel>Total Revenue</StatLabel>
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon $color="#0f766e">
              <FaMoneyBillWave />
            </StatIcon>
            <StatContent>
              <StatValue>{formatCurrency(statistics.totalCollected)}</StatValue>
              <StatLabel>Total Collected</StatLabel>
            </StatContent>
          </StatCard>

          <StatCard>
            <StatIcon $color="#dc2626">
              <FaChartLine />
            </StatIcon>
            <StatContent>
              <StatValue>
                {formatCurrency(statistics.outstandingBalance)}
              </StatValue>
              <StatLabel>Outstanding Balance</StatLabel>
            </StatContent>
          </StatCard>
        </StatsGrid>
      )}

      <QuickLinksGrid>
        <QuickLinkCard to="/admin/camp-registration">
          <FaUsers />
          <div>
            <strong>Registrations</strong>
            <span>Manage camper records and payments.</span>
          </div>
        </QuickLinkCard>

        <QuickLinkCard to="/admin/camp-qr-codes">
          <FaQrcode />
          <div>
            <strong>QR Codes</strong>
            <span>Open and download camper QR codes.</span>
          </div>
        </QuickLinkCard>

        <QuickLinkCard to="/admin/camp-checkins">
          <FaClipboardCheck />
          <div>
            <strong>Check-Ins</strong>
            <span>Scan and review daily attendance.</span>
          </div>
        </QuickLinkCard>
      </QuickLinksGrid>

      {statistics && (
        <TwoColumnGrid>
          <Card>
            <CardHeader>
              <CardTitle>Payment Status</CardTitle>
            </CardHeader>
            <CardBody>
              <BreakdownGrid>
                <BreakdownCard $tone="danger">
                  <strong>
                    {statistics.paymentStatusBreakdown?.pending || 0}
                  </strong>
                  <span>Pending</span>
                </BreakdownCard>
                <BreakdownCard $tone="warning">
                  <strong>
                    {statistics.paymentStatusBreakdown?.partial || 0}
                  </strong>
                  <span>Partial</span>
                </BreakdownCard>
                <BreakdownCard $tone="success">
                  <strong>
                    {statistics.paymentStatusBreakdown?.completed || 0}
                  </strong>
                  <span>Completed</span>
                </BreakdownCard>
              </BreakdownGrid>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Check-In Snapshot</CardTitle>
            </CardHeader>
            <CardBody>
              <SnapshotGrid>
                <SnapshotCard>
                  <strong>
                    {selectedDaySummary?.checkedInCount || 0}
                  </strong>
                  <span>
                    Checked in for {selectedDaySummary?.dayLabel || 'today'}
                  </span>
                </SnapshotCard>
                <SnapshotCard>
                  <strong>
                    {selectedDaySummary?.outstandingCount || 0}
                  </strong>
                  <span>Still expected</span>
                </SnapshotCard>
                <SnapshotCard>
                  <strong>
                    {checkInOverview?.summary?.totalCheckIns || 0}
                  </strong>
                  <span>Total scans logged</span>
                </SnapshotCard>
              </SnapshotGrid>
            </CardBody>
          </Card>
        </TwoColumnGrid>
      )}

      {checkInOverview?.daySummaries?.length > 0 && (
        <Card style={{ marginTop: '1.5rem' }}>
          <CardHeader>
            <CardTitle>Daily Check-In Progress</CardTitle>
          </CardHeader>
          <CardBody>
            <DayGrid>
              {checkInOverview.daySummaries.map((day) => (
                <DayCard
                  key={day.dayKey}
                  $active={day.dayKey === checkInOverview?.selectedDay?.dayKey}
                >
                  <h3>{day.dayLabel}</h3>
                  <p>{day.dateLabel}</p>
                  <strong>
                    {day.checkedInCount}/{day.totalParticipants}
                  </strong>
                  <span>{day.outstandingCount} remaining</span>
                </DayCard>
              ))}
            </DayGrid>
          </CardBody>
        </Card>
      )}

      {statistics?.roomOccupancy?.length > 0 && (
        <Card style={{ marginTop: '1.5rem' }}>
          <CardHeader>
            <CardTitle>
              <FaBed /> Room Occupancy
            </CardTitle>
          </CardHeader>
          <CardBody>
            <OccupancyGrid>
              {statistics.roomOccupancy.map((room) => (
                <OccupancyCard key={room.name}>
                  <strong>{room.name}</strong>
                  <span>
                    {room.occupied}/{room.total} beds occupied
                  </span>
                  <OccupancyBar>
                    <OccupancyFill
                      style={{ width: `${Number(room.occupancyRate || 0)}%` }}
                    />
                  </OccupancyBar>
                  <small>{room.occupancyRate}% full</small>
                </OccupancyCard>
              ))}
            </OccupancyGrid>
          </CardBody>
        </Card>
      )}
    </AdminLayout>
  );
};

const LoadingState = styled.div`
  min-height: 280px;
  display: grid;
  place-items: center;
  color: #64748b;
  font-weight: 600;
`;

const PageToolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
`;

const ToolbarText = styled.p`
  margin: 0;
  color: #475569;
  max-width: 640px;
`;

const QuickLinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const QuickLinkCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.2rem;
  border-radius: 18px;
  text-decoration: none;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  color: #0f172a;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.1);
  }

  svg {
    font-size: 1.5rem;
    color: #2563eb;
  }

  strong,
  span {
    display: block;
  }

  span {
    margin-top: 0.25rem;
    color: #64748b;
    font-size: 0.92rem;
  }
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const BreakdownGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const BreakdownCard = styled.div`
  border-radius: 16px;
  padding: 1rem;
  background: ${(props) => {
    if (props.$tone === 'success') return '#ecfdf5';
    if (props.$tone === 'warning') return '#fffbeb';
    return '#fef2f2';
  }};
  border: 1px solid
    ${(props) => {
      if (props.$tone === 'success') return '#bbf7d0';
      if (props.$tone === 'warning') return '#fde68a';
      return '#fecaca';
    }};

  strong,
  span {
    display: block;
  }

  strong {
    color: #0f172a;
    font-size: 1.5rem;
  }

  span {
    margin-top: 0.35rem;
    color: #475569;
    font-size: 0.92rem;
    font-weight: 600;
  }
`;

const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
`;

const SnapshotCard = styled.div`
  border-radius: 16px;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;

  strong,
  span {
    display: block;
  }

  strong {
    color: #0f172a;
    font-size: 1.4rem;
  }

  span {
    margin-top: 0.35rem;
    color: #64748b;
    font-size: 0.92rem;
  }
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const DayCard = styled.div`
  border-radius: 18px;
  padding: 1.1rem;
  border: 1px solid ${(props) => (props.$active ? '#bfdbfe' : '#e2e8f0')};
  background: ${(props) =>
    props.$active
      ? 'linear-gradient(180deg, #eff6ff 0%, #ffffff 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'};

  h3,
  p,
  strong,
  span {
    margin: 0;
    display: block;
  }

  h3 {
    color: #0f172a;
    font-size: 1rem;
  }

  p,
  span {
    margin-top: 0.35rem;
    color: #64748b;
    font-size: 0.92rem;
  }

  strong {
    margin-top: 0.9rem;
    color: #1d4ed8;
    font-size: 1.5rem;
  }
`;

const OccupancyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
`;

const OccupancyCard = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1rem;
  background: #fff;

  strong,
  span,
  small {
    display: block;
  }

  strong {
    color: #0f172a;
  }

  span,
  small {
    margin-top: 0.35rem;
    color: #64748b;
  }
`;

const OccupancyBar = styled.div`
  height: 10px;
  margin-top: 0.9rem;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
`;

const OccupancyFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2563eb 0%, #38bdf8 100%);
`;

export default CampAdminDashboard;
